import { Placement } from '@popperjs/core';
import React, { MouseEventHandler, useRef, useState } from 'react';
import { Content } from 'src/common/interfaces/content.interface';
import { JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { EditorConfirmPanel } from 'src/editor/confirm-panel';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { apiDeleteContent } from 'src/utils/journeyApi';
import { LibraryContentCardMenu } from '.';
import { LibraryContentTagsPanel } from './tags-panel';

type LibraryContentCardMenuOptionsActionPanelTypes = Nullable<'delete' | 'tags'>;

interface Props extends Pick<JourneyMenuPropsInterface, 'placement'> {
  contentId: Content['id'];
  onContentDelete: (contentId: Content['id']) => void;
  onTagsUpdate: (content: Content) => void;
  usingJourneyCount: number;
  onMenuOpen: (placement?: Placement) => void;
  onMenuClose: () => void;
  renderMenuOrigin: (onClick: MouseEventHandler<HTMLElement>) => any;
}

const getDeleteConfirmCopyForUsedContent = (journeyCount: number) => {
  if (journeyCount <= 0) {
    return 'Deleting this content is an irreversible action';
  }

  if (journeyCount === 1) {
    return 'This content is used in 1 Journey - deleting it will remove it from that Journey and is an irreversible action';
  }

  return `This content is used across ${journeyCount} Journeys - deleting it will remove it from those Journeys and is an irreversible action`;
};

export const LibraryContentCardMenuOptions = ({
  contentId,
  usingJourneyCount,
  onMenuOpen,
  placement,
  onContentDelete,
  onTagsUpdate,
  onMenuClose,
  renderMenuOrigin,
}: Props) => {
  const [activePanel, setActivePanel] = useState<LibraryContentCardMenuOptionsActionPanelTypes>(null);

  const menuOpenRef = useRef(functionNoop);
  const menuCloseRef = useRef(functionNoop);

  const deletePanelOpenRef = useRef(functionNoop);
  const deletePanelCloseRef = useRef(functionNoop);

  const tagsPanelOpenRef = useRef(functionNoop);
  const tagsPanelCloseRef = useRef(functionNoop);

  const onMenuClicked = () => {
    menuOpenRef.current();
  };

  const onBackClicked = () => {
    setActivePanel(null);
    setTimeout(onMenuClicked, 50);
  };

  const onDeletePanelClosed = () => {
    setActivePanel(null);
    deletePanelCloseRef.current();
    onMenuClose();
    return contentId;
  };

  const onDeletePanelConfirmed = () => {
    apiDeleteContent(contentId).then(onDeletePanelClosed).then(onContentDelete);
  };

  if (activePanel === 'delete') {
    return (
      <EditorConfirmPanel
        key={activePanel}
        placement={placement}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        openRef={deletePanelOpenRef}
        closeRef={deletePanelCloseRef}
        copy={getDeleteConfirmCopyForUsedContent(usingJourneyCount)}
        confirmCopy='Yes, delete'
        denyCopy='No, keep'
        onOpen={onMenuOpen}
        fixed
        onClose={onDeletePanelClosed}
        onConfirm={onDeletePanelConfirmed}
        onDeny={onDeletePanelClosed}
        onBack={onBackClicked}
      />
    );
  }

  if (activePanel === 'tags') {
    return (
      <LibraryContentTagsPanel
        key={activePanel}
        contentId={contentId}
        placement={placement}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
        openRef={tagsPanelOpenRef}
        closeRef={tagsPanelCloseRef}
        onTagsUpdate={onTagsUpdate}
        onOpen={onMenuOpen}
        onClose={onMenuClose}
        onBack={onBackClicked}
      />
    );
  }

  return (
    <LibraryContentCardMenu
      placement='bottom-start'
      renderMenuOrigin={renderMenuOrigin}
      onOpen={onMenuOpen}
      onClose={onMenuClose}
      openRef={menuOpenRef}
      closeRef={menuCloseRef}
      onDeleteClick={() => {
        setActivePanel('delete');
      }}
      onTagsClick={() => {
        setActivePanel('tags');
      }}
    />
  );
};
